import React, { useEffect, useState } from 'react'
import { getAssociatedStations } from '../../actions/contentActions.js'
import LiveTVUnavailable from './LiveTVUnavailable'
import LiveTVWithStations from './LiveTVWithStations'
import LiveTVWithEPG from './LiveTVWithEPG'
import Spinner from '../../components/Spinner'
import { useLocation, useNavigate } from 'react-router'
import { useAppDispatch, useAppSelector } from '../../hooks/useStore'
import { useSidePanel } from '../../components/SidePanel/SidePanelProvider'

import type { Station } from './types'

const LivestreamScreen = () => {
  const [autoFocusEPG, setAutoFocusEPG] = useState(false)
  const [stationsLoaded, setStationsLoaded] = useState(false)
  const [stations, setStations] = useState<Array<Station>>([])
  const [isUserInAssociatedStations, setIsUserInAssociatedStations] =
    useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const callSign = useAppSelector((state) => state.station.call_sign)
  const fallbackStation = useAppSelector(
    (state) => state.station.fallback_station
  )
  const dispatch = useAppDispatch()
  const { keepSidePanelOpened } = useSidePanel()

  const isScreenOpenedFromChannelPlayback = () =>
    location.search.includes('fromChannelPlayback=true')

  const clearFallbackStation = () => {
    dispatch({
      type: 'UPDATE_FALLBACK_STATION',
      payload: {
        callsign: '',
        cid: '',
        city: '',
        fullName: '',
        shortName: '',
      },
    })
  }

  useEffect(() => {
    keepSidePanelOpened(true)
    if (!isScreenOpenedFromChannelPlayback()) {
      clearFallbackStation()
    }
    userInAssociatedStations()
    return () => keepSidePanelOpened(false)
  }, [])

  const userInAssociatedStations = () => {
    const { watchChannelFromHome } = location.state || {}
    getAssociatedStations()
      .then(({ stations } = { stations: [] }) => {
        const isUserInAssociatedStations = stations
          .map((station) => station.callsign)
          .includes(callSign)

        if (watchChannelFromHome) {
          if (isUserInAssociatedStations) {
            navigate('/live-playback', {
              state: {
                channel: watchChannelFromHome,
              },
              replace: true,
            })
            return
          }
        }

        setStationsLoaded(true)
        setIsUserInAssociatedStations(isUserInAssociatedStations)
        setStations(
          stations.map((x) => {
            return {
              callsign: x.callsign,
              cid: x.station_id,
              city: x.city,
              fullName: x.common_name_full,
              shortName: x.common_name_short,
            }
          })
        )
      })
      .catch(() => {
        setStationsLoaded(true)
      })
  }

  const stationName =
    stations?.find((station) => station.callsign === callSign)?.shortName || ''

  const { callsign: fallbackStationCallsign, shortName: fallbackStationName } =
    fallbackStation

  return !stationsLoaded ? (
    <Spinner />
  ) : (
    <div className="livestream-screen">
      {!isUserInAssociatedStations &&
        stations.length > 0 &&
        (!fallbackStationCallsign ? (
          <LiveTVWithStations
            stations={stations}
            setAutoFocusEPG={setAutoFocusEPG}
          />
        ) : (
          <LiveTVWithEPG
            autoFocus={autoFocusEPG}
            stationName={fallbackStationName}
            setAutoFocusEPG={setAutoFocusEPG}
          />
        ))}
      {isUserInAssociatedStations && (
        <LiveTVWithEPG
          autoFocus={autoFocusEPG}
          stationName={stationName}
          setAutoFocusEPG={setAutoFocusEPG}
        />
      )}
      {stations.length === 0 && <LiveTVUnavailable navigate={navigate} />}
    </div>
  )
}

export default LivestreamScreen
